/** Long-form blog / product overview content. Keys pair with `useKivunovaTranslation` fallbacks. */
import type { BlogSection, BlogText } from "../types";

const t = (key: string, fallback: string): BlogText => ({ key, fallback });

export const BLOG_INTRO_PARAGRAPHS: BlogText[] = [
  t(
    "blog-intro-1",
    "Running a business means tracking sales, stock, payments, customers, and stores. When those records live in notebooks or scattered files, mistakes happen and decisions get harder."
  ),
  t("blog-intro-2", "That is where Kivunova FMS comes in."),
  t(
    "blog-intro-3",
    "Kivunova FMS is a modern Financial and Inventory Management System designed to help businesses manage their operations in one organized, simple, and reliable platform. It brings together financial tracking, inventory management, store management, order processing, payment records, customer and supplier records, team access, reporting, subscriptions, and business insights—so owners and teams can understand what is happening in their business at any time."
  ),
  t(
    "blog-intro-4",
    "Whether you manage a retail shop, pharmacy, warehouse, restaurant, school, supermarket, organization, or a growing company with multiple stores, Kivunova FMS helps you reduce manual work, improve accuracy, and make better decisions with confidence."
  )
];

const countryTable: BlogTable = {
  headerKeys: ["blog-countries-table-country", "blog-countries-table-currency"],
  headerFallbacks: ["Country", "Currency"],
  rows: [
    {
      rowKey: "blog-countries-row-ke",
      cells: ["Kenya", "Kenyan Shilling (KES)"]
    },
    {
      rowKey: "blog-countries-row-ug",
      cells: ["Uganda", "Ugandan Shilling (UGX)"]
    },
    {
      rowKey: "blog-countries-row-tz",
      cells: ["Tanzania", "Tanzanian Shilling (TZS)"]
    },
    {
      rowKey: "blog-countries-row-bi",
      cells: ["Burundi", "Burundi Franc (BIF)"]
    },
    {
      rowKey: "blog-countries-row-cd",
      cells: ["DR Congo", "Congolese Franc"]
    }
  ]
};

export const BLOG_SECTIONS: BlogSection[] = [
  {
    id: "what-is",
    titleKey: "blog-what-is-title",
    titleFallback: "What Is Kivunova FMS?",
    leadParagraphs: [
      t(
        "blog-what-is-p1",
        "Kivunova FMS is a business management platform built to simplify how companies manage their financial and inventory operations."
      ),
      t(
        "blog-what-is-p2",
        "Instead of depending on paper notebooks, spreadsheets, manual calculations, or scattered records, businesses can use Kivunova FMS to keep their important information in one place."
      ),
      t("blog-what-is-p3", "With Kivunova FMS, businesses can manage:")
    ],
    listItems: [
      t("blog-what-is-li-1", "Products and inventory"),
      t("blog-what-is-li-2", "Stock movement and availability"),
      t("blog-what-is-li-3", "Stores and branches"),
      t("blog-what-is-li-4", "Sales and purchase orders"),
      t("blog-what-is-li-5", "Invoices, payments, and receipts"),
      t("blog-what-is-li-6", "Customers and suppliers"),
      t("blog-what-is-li-7", "Employees and team access"),
      t("blog-what-is-li-8", "Revenue, VAT, and financial summaries"),
      t("blog-what-is-li-9", "Company information and settings"),
      t("blog-what-is-li-10", "Subscriptions and plans"),
      t("blog-what-is-li-11", "Reports and business performance"),
      t("blog-what-is-li-12", "AI-powered assistance through Ask Teta")
    ],
    tailParagraphs: [
      t(
        "blog-what-is-p4",
        "The goal is simple: help businesses operate with clarity, control, and confidence."
      )
    ]
  },
  {
    id: "where-operates",
    titleKey: "blog-where-operates-title",
    titleFallback: "Where Kivunova FMS Operates",
    paragraphs: [
      t(
        "blog-where-operates-p1",
        "Kivunova FMS is built for businesses in East and Central Africa, with support for local currencies, phone formats, and everyday languages used in the region."
      )
    ],
    subsections: [
      {
        id: "currently-operating",
        titleKey: "blog-where-currently-title",
        titleFallback: "Currently operating",
        paragraphs: [
          t(
            "blog-where-currently-p1",
            "Rwanda is the primary market where Kivunova FMS is actively available today. Businesses in Rwanda can register, set up their company in Rwandan Francs (RWF), configure VAT, manage stores across the country, and subscribe to plans using local payment options such as mobile money and card checkout."
          ),
          t(
            "blog-where-currently-p2",
            "Kivunova FMS supports retailers, wholesalers, pharmacies, warehouses, and growing multi-store businesses operating in Rwanda."
          )
        ]
      },
      {
        id: "expanding",
        titleKey: "blog-where-expanding-title",
        titleFallback: "Expanding across the region",
        paragraphs: [
          t(
            "blog-where-expanding-p1",
            "The platform is also built to support businesses in:"
          ),
          t(
            "blog-where-expanding-p2",
            "Availability of registration, billing, and payment options may vary by country as Kivunova expands. If your business operates outside Rwanda, contact Kivunova to confirm current availability in your location."
          )
        ],
        table: countryTable
      },
      {
        id: "local-needs",
        titleKey: "blog-where-local-title",
        titleFallback: "Built for local business needs",
        paragraphs: [
          t(
            "blog-where-local-p1",
            "Across supported countries, Kivunova FMS is designed for real local conditions:"
          )
        ],
        listItems: [
          t(
            "blog-where-local-li-1",
            "Company currency set during setup (for example RWF, KES, or UGX)"
          ),
          t(
            "blog-where-local-li-2",
            "VAT and tax settings where applicable—especially for Rwanda"
          ),
          t(
            "blog-where-local-li-3",
            "Multilingual use for teams that work in more than one language"
          ),
          t(
            "blog-where-local-li-4",
            "Access from the office, the shop floor, or on the go through a web browser"
          )
        ]
      }
    ]
  },
  {
    id: "languages",
    titleKey: "blog-languages-title",
    titleFallback: "Work in Your Language",
    paragraphs: [
      t(
        "blog-languages-p1",
        "Kivunova FMS is built for teams across East and Central Africa. The platform supports:"
      ),
      t(
        "blog-languages-p2",
        "You can switch language from the app and work in the language your team is most comfortable with. This makes daily operations easier for owners, cashiers, store managers, and finance staff who may not all use the same language at work."
      )
    ],
    listItems: [
      t("blog-languages-li-1", "English"),
      t("blog-languages-li-2", "French"),
      t("blog-languages-li-3", "Kinyarwanda"),
      t("blog-languages-li-4", "Swahili")
    ]
  },
  {
    id: "why-need",
    titleKey: "blog-why-need-title",
    titleFallback: "Why Businesses Need a System Like Kivunova FMS",
    leadParagraphs: [
      t(
        "blog-why-need-p1",
        "Many businesses start with simple tools. A notebook, a spreadsheet, or a basic receipt book may be enough in the beginning. But as the business grows, these methods become harder to manage."
      ),
      t("blog-why-need-p2", "Common challenges include:")
    ],
    listItems: [
      t(
        "blog-why-need-li-1",
        "Not knowing exactly how much stock is available"
      ),
      t("blog-why-need-li-2", "Losing track of sales and payments"),
      t("blog-why-need-li-3", "Recording information incorrectly"),
      t("blog-why-need-li-4", "Forgetting customer debts or unpaid orders"),
      t("blog-why-need-li-5", "Spending too much time preparing reports"),
      t("blog-why-need-li-6", "Making decisions without clear financial data"),
      t("blog-why-need-li-7", "Managing multiple stores manually"),
      t(
        "blog-why-need-li-8",
        "Losing business information when records are damaged or misplaced"
      ),
      t(
        "blog-why-need-li-9",
        "Depending on one person who knows where everything is written"
      )
    ],
    tailParagraphs: [
      t(
        "blog-why-need-p3",
        "These challenges can lead to confusion, lost revenue, poor planning, and slower growth."
      ),
      t(
        "blog-why-need-p4",
        "Kivunova FMS helps solve these problems by giving businesses a clear digital system for managing their daily operations."
      )
    ]
  },
  {
    id: "inventory",
    titleKey: "blog-inventory-title",
    titleFallback: "Inventory Management Made Simple",
    paragraphs: [
      t(
        "blog-inventory-p1",
        "Inventory is one of the most important parts of many businesses. If a business does not know what products are available, what is running low, or what is moving fast, it becomes difficult to serve customers and control costs."
      ),
      t(
        "blog-inventory-p2",
        "Kivunova FMS helps businesses manage inventory more effectively by allowing teams to track products, stores, and stock movement."
      )
    ],
    subsections: [
      {
        id: "products-stock",
        titleKey: "blog-inventory-products-title",
        titleFallback: "Products and stock",
        paragraphs: [
          t(
            "blog-inventory-products-p1",
            "In Kivunova FMS, your product catalog is what you sell—the name, category, price context, and how the item is sold. Stock is tracked at a detailed level so you know exactly what is available in each store, not just a rough count on paper."
          ),
          t(
            "blog-inventory-products-p2",
            "With inventory management in Kivunova FMS, businesses can:"
          ),
          t(
            "blog-inventory-products-p3",
            "This gives business owners better visibility and helps store managers work more efficiently. Instead of guessing what is available, teams can rely on up-to-date information."
          )
        ],
        listItems: [
          t("blog-inventory-products-li-1", "Add and manage products"),
          t("blog-inventory-products-li-2", "Organize products by category"),
          t("blog-inventory-products-li-3", "Track available stock by store"),
          t(
            "blog-inventory-products-li-4",
            "Monitor stock movement as sales and purchases happen"
          ),
          t(
            "blog-inventory-products-li-5",
            "Know what enters and leaves the store"
          ),
          t(
            "blog-inventory-products-li-6",
            "See when items are reserved for an order or already sold"
          ),
          t(
            "blog-inventory-products-li-7",
            "Receive low-stock alerts when products are running low"
          ),
          t("blog-inventory-products-li-8", "Reduce stock-related mistakes"),
          t("blog-inventory-products-li-9", "Improve planning for restocking"),
          t(
            "blog-inventory-products-li-10",
            "Understand which products are active or inactive"
          )
        ]
      },
      {
        id: "selling-ways",
        titleKey: "blog-inventory-selling-title",
        titleFallback: "Different ways to sell products",
        paragraphs: [
          t(
            "blog-inventory-selling-p1",
            "Not every business sells the same way. Kivunova FMS supports:"
          ),
          t(
            "blog-inventory-selling-p2",
            "This flexibility helps retail shops, pharmacies, warehouses, and food-related businesses record sales the way they actually happen."
          )
        ],
        listItems: [
          t(
            "blog-inventory-selling-li-1",
            "Single items — such as one bottle, one box, or one packaged unit"
          ),
          t(
            "blog-inventory-selling-li-2",
            "Items sold by count — where the cashier confirms quantity at checkout"
          ),
          t(
            "blog-inventory-selling-li-3",
            "Items sold by measure — such as weight or volume (for example kilograms or liters)"
          )
        ]
      }
    ]
  },
  {
    id: "stores",
    titleKey: "blog-stores-title",
    titleFallback: "Store and Branch Management",
    leadParagraphs: [
      t(
        "blog-stores-p1",
        "Many businesses operate from more than one location. A company may have multiple shops, warehouses, branches, or service points. Managing these locations manually can become complicated."
      ),
      t(
        "blog-stores-p2",
        "Kivunova FMS supports store management by helping businesses organize their different locations in one system."
      ),
      t("blog-stores-p3", "Businesses can use the platform to:")
    ],
    listItems: [
      t("blog-stores-li-1", "Create and manage stores"),
      t("blog-stores-li-2", "View store details and contacts"),
      t("blog-stores-li-3", "Track activity by store"),
      t("blog-stores-li-4", "Organize stock by location"),
      t("blog-stores-li-5", "Assign staff and operations to the right branch"),
      t("blog-stores-li-6", "Search, filter, and export store information")
    ],
    tailParagraphs: [
      t(
        "blog-stores-p4",
        "This is useful for business owners who need a clear view of performance across different locations."
      ),
      t(
        "blog-stores-p5",
        "A multi-store business needs more than just sales numbers. It needs to know which store is performing well, where products are located, and how each branch contributes to the overall business."
      ),
      t("blog-stores-p6", "Kivunova FMS helps bring that visibility together.")
    ]
  },
  {
    id: "customers-suppliers",
    titleKey: "blog-crm-title",
    titleFallback: "Customers and Suppliers",
    paragraphs: [
      t(
        "blog-crm-p1",
        "Strong businesses depend on good relationships—with buyers and with the people who supply their stock."
      )
    ],
    subsections: [
      {
        id: "customers",
        titleKey: "blog-customers-title",
        titleFallback: "Customer management",
        paragraphs: [
          t(
            "blog-customers-p1",
            "Kivunova FMS helps you keep customer records organized:"
          ),
          t(
            "blog-customers-p2",
            "When customer information lives in the same system as your orders and payments, it is easier to answer questions, follow up on unpaid amounts, and serve repeat buyers with confidence."
          )
        ],
        listItems: [
          t("blog-customers-li-1", "Record customer names and contact details"),
          t("blog-customers-li-2", "Track individual and business customers"),
          t(
            "blog-customers-li-3",
            "Store tax identification (TIN) for business buyers where needed"
          ),
          t("blog-customers-li-4", "Link customers to the right store"),
          t("blog-customers-li-5", "Support better order history and follow-up")
        ]
      },
      {
        id: "suppliers",
        titleKey: "blog-suppliers-title",
        titleFallback: "Supplier management",
        paragraphs: [
          t(
            "blog-suppliers-p1",
            "You can also manage the suppliers you buy from:"
          ),
          t(
            "blog-suppliers-p2",
            "This helps purchasing teams and store managers stay organized when restocking or placing purchase orders."
          )
        ],
        listItems: [
          t("blog-suppliers-li-1", "Create and maintain supplier profiles"),
          t("blog-suppliers-li-2", "Store contact and address details"),
          t("blog-suppliers-li-3", "Link suppliers to stores"),
          t("blog-suppliers-li-4", "Search and review supplier records")
        ]
      }
    ]
  },
  {
    id: "orders",
    titleKey: "blog-orders-title",
    titleFallback: "Orders, Sales, and Payments in One Place",
    paragraphs: [
      t(
        "blog-orders-p1",
        "Every sale matters. Every payment matters. Every receipt matters."
      ),
      t(
        "blog-orders-p2",
        "Kivunova FMS helps businesses manage orders and payments in a structured way. This makes it easier to understand what was sold, who made the purchase, which store handled the transaction, how much was paid, and what remains outstanding."
      )
    ],
    subsections: [
      {
        id: "sales-purchases",
        titleKey: "blog-orders-sales-title",
        titleFallback: "Sales and purchases",
        paragraphs: [
          t(
            "blog-orders-sales-p1",
            "The platform supports both sales (what you sell to customers) and purchases (what you buy from suppliers). Depending on your workflow, you can move from an order to an invoice, record a payment, and generate a receipt—keeping the full picture in one place."
          ),
          t("blog-orders-sales-p2", "With Kivunova FMS, businesses can:"),
          t(
            "blog-orders-sales-p3",
            "When items are added to an order, stock can be reserved until the sale is completed—helping reduce double-selling and confusion at busy times."
          ),
          t(
            "blog-orders-sales-p4",
            "This reduces confusion between what was ordered, what was paid, and what needs follow-up."
          ),
          t(
            "blog-orders-sales-p5",
            "For cashiers, this creates a smoother workflow. For accountants, this creates better records. For business owners, this creates stronger visibility into revenue and customer activity."
          )
        ],
        listItems: [
          t(
            "blog-orders-sales-li-1",
            "Create and manage sales orders and purchase orders"
          ),
          t(
            "blog-orders-sales-li-2",
            "Record sales at the counter or back office"
          ),
          t(
            "blog-orders-sales-li-3",
            "Scan barcodes or QR codes to add items quickly at checkout"
          ),
          t("blog-orders-sales-li-4", "Track payment status"),
          t("blog-orders-sales-li-5", "Generate invoices and receipts"),
          t("blog-orders-sales-li-6", "View order details"),
          t("blog-orders-sales-li-7", "Print order documents"),
          t(
            "blog-orders-sales-li-8",
            "Monitor totals, discounts, taxes, and final amounts"
          ),
          t("blog-orders-sales-li-9", "Keep sales and payments connected")
        ]
      }
    ]
  },
  {
    id: "financial-reporting",
    titleKey: "blog-financial-title",
    titleFallback: "Financial Management and Business Reporting",
    leadParagraphs: [
      t(
        "blog-financial-p1",
        "A business cannot grow confidently without understanding its numbers."
      ),
      t(
        "blog-financial-p2",
        "Kivunova FMS gives business leaders a clearer view of financial performance. Instead of waiting for manual calculations or scattered reports, users can view important financial indicators directly in the system."
      ),
      t("blog-financial-p3", "The platform can help businesses understand:")
    ],
    listItems: [
      t("blog-financial-li-1", "Total revenue"),
      t("blog-financial-li-2", "VAT to pay"),
      t("blog-financial-li-3", "Net revenue"),
      t("blog-financial-li-4", "Payments received"),
      t("blog-financial-li-5", "Sales performance"),
      t("blog-financial-li-6", "Order and invoice status"),
      t("blog-financial-li-7", "Business trends over time")
    ],
    tailParagraphs: [
      t(
        "blog-financial-p4",
        "Clear reports help owners and managers make better decisions."
      )
    ],
    subsections: [
      {
        id: "example-questions",
        titleKey: "blog-financial-questions-title",
        titleFallback: "For example, a business owner may want to know:",
        listItems: [
          t("blog-financial-q-li-1", "Are we making more revenue this month?"),
          t("blog-financial-q-li-2", "Which store is performing better?"),
          t("blog-financial-q-li-3", "How much VAT should we expect?"),
          t("blog-financial-q-li-4", "Are we growing or losing momentum?"),
          t(
            "blog-financial-q-li-5",
            "Which products contribute most to sales?"
          ),
          t(
            "blog-financial-q-li-6",
            "How many orders are still open or unpaid?"
          )
        ],
        paragraphs: [
          t(
            "blog-financial-p6",
            "Kivunova FMS helps answer these questions by turning business data into useful insights through dashboards and charts."
          )
        ]
      },
      {
        id: "expenses-note",
        titleKey: "blog-financial-expenses-title",
        titleFallback: "A note on expenses and full accounting",
        paragraphs: [
          t(
            "blog-financial-expenses-p1",
            "Kivunova FMS gives strong visibility into sales, payments, VAT, and revenue-related performance. Dedicated expense tracking and full cost-of-goods accounting are areas the platform continues to grow into. Today, profit-related views focus mainly on revenue and tax-related amounts from your recorded sales activity—not every type of business cost you might track in a full accounting system."
          )
        ]
      }
    ]
  },
  {
    id: "vat-currency",
    titleKey: "blog-vat-title",
    titleFallback: "VAT and Local Currency Support",
    paragraphs: [
      t(
        "blog-vat-p1",
        "Kivunova FMS is designed with local business realities in mind."
      ),
      t(
        "blog-vat-p2",
        "For businesses operating in Rwanda, financial records often need to work clearly with RWF and VAT. Kivunova FMS supports RWF-based financial tracking and company-level settings such as:"
      ),
      t(
        "blog-vat-p3",
        "Businesses in other supported countries can set their company currency during registration (for example KES, UGX, or TZS)."
      ),
      t(
        "blog-vat-p4",
        "This makes the system more practical for local businesses that need to manage revenue, tax-related amounts, invoices, reports, and financial summaries in a familiar way."
      )
    ],
    listItems: [
      t("blog-vat-li-1", "VAT percentage"),
      t("blog-vat-li-2", "VAT inclusive pricing (price already includes VAT)"),
      t("blog-vat-li-3", "VAT exclusive pricing (VAT added on top)")
    ]
  },
  {
    id: "dashboards",
    titleKey: "blog-dashboards-title",
    titleFallback: "Dashboards and Business Insights",
    paragraphs: [
      t(
        "blog-dashboards-p1",
        "The dashboard is your daily command center. After you sign in, you can see key numbers and charts that summarize how your business is doing—without building spreadsheets by hand."
      ),
      t(
        "blog-dashboards-p2",
        "Depending on your role and plan, you may see insights about:"
      ),
      t(
        "blog-dashboards-p3",
        "Company administrators can often filter by date range and by store, so multi-location owners can compare performance across branches."
      )
    ],
    listItems: [
      t("blog-dashboards-li-1", "Revenue and financial performance"),
      t("blog-dashboards-li-2", "Orders and invoices"),
      t("blog-dashboards-li-3", "Products and categories"),
      t("blog-dashboards-li-4", "Inventory movement and stock status"),
      t("blog-dashboards-li-5", "Stores and branches"),
      t("blog-dashboards-li-6", "Customers and suppliers"),
      t("blog-dashboards-li-7", "Employees and team activity"),
      t("blog-dashboards-li-8", "Your subscription and plan status")
    ]
  },
  {
    id: "ask-teta",
    titleKey: "blog-teta-title",
    titleFallback: "Ask Teta: Your Business Assistant",
    paragraphs: [
      t(
        "blog-teta-p1",
        "Kivunova FMS includes Ask Teta, an AI-powered assistant designed to help users interact with the system more easily."
      ),
      t("blog-teta-p2", "Ask Teta can support users by helping them:"),
      t(
        "blog-teta-p3",
        "Instead of feeling stuck, users can ask questions and receive helpful support directly within the platform."
      ),
      t("blog-teta-p4", "Examples of what you can ask:"),
      t(
        "blog-teta-p5",
        "Ask Teta adds a smarter layer to the system by making business management more conversational, accessible, and user-friendly."
      ),
      t(
        "blog-teta-p6",
        "This is especially useful for users who may not be technical or who need quick help while managing daily operations."
      )
    ],
    listItems: [
      t("blog-teta-help-li-1", "Understand how to use the platform"),
      t(
        "blog-teta-help-li-2",
        "Find information about products, orders, and stock"
      ),
      t("blog-teta-help-li-3", "Get answers about business performance"),
      t(
        "blog-teta-help-li-4",
        "Receive guidance while managing daily operations"
      ),
      t("blog-teta-example-li-1", "What products are low on stock?"),
      t("blog-teta-example-li-2", "Show my recent orders."),
      t("blog-teta-example-li-3", "What is my revenue this month?"),
      t("blog-teta-example-li-4", "How do I add a new product?")
    ]
  },
  {
    id: "subscriptions",
    titleKey: "blog-subscriptions-title",
    titleFallback: "Subscription and Plan Management",
    paragraphs: [
      t(
        "blog-subscriptions-p1",
        "Kivunova FMS also includes subscription management features that help companies manage their access to the platform."
      ),
      t(
        "blog-subscriptions-p2",
        "Businesses can view their subscription details, plan status, billing information, and available plans. This gives users clarity about their current package and helps them choose a plan that matches their business needs."
      ),
      t(
        "blog-subscriptions-p3",
        "Plans can include different capabilities, such as:"
      ),
      t("blog-subscriptions-p4", "The system may include features such as:"),
      t(
        "blog-subscriptions-p5",
        "In Rwanda, subscription payments can be completed using mobile money and card options through integrated checkout."
      ),
      t(
        "blog-subscriptions-p6",
        "This makes it easier for businesses to start, test, and grow with Kivunova FMS."
      )
    ],
    listItems: [
      t("blog-subscriptions-cap-li-1", "Product and catalog management"),
      t("blog-subscriptions-cap-li-2", "Detailed item-level stock tracking"),
      t("blog-subscriptions-cap-li-3", "QR and barcode scanning at checkout"),
      t("blog-subscriptions-cap-li-4", "Reporting and analytics"),
      t("blog-subscriptions-cap-li-5", "Multi-store management"),
      t("blog-subscriptions-feat-li-1", "Active subscription status"),
      t("blog-subscriptions-feat-li-2", "Plan details"),
      t(
        "blog-subscriptions-feat-li-3",
        "Pricing in local currency (for example RWF)"
      ),
      t("blog-subscriptions-feat-li-4", "Billing dates"),
      t("blog-subscriptions-feat-li-5", "7-day free trial on available plans"),
      t("blog-subscriptions-feat-li-6", "Upgrade or plan selection options")
    ]
  },
  {
    id: "people-roles",
    titleKey: "blog-people-title",
    titleFallback: "People, Roles, and Team Access",
    paragraphs: [
      t(
        "blog-people-p1",
        "Kivunova FMS is not only for accountants. It is built for the people who run and support the business every day."
      ),
      t(
        "blog-people-p2",
        "The platform supports different roles so the right people see the right information:"
      ),
      t(
        "blog-people-p3",
        "Each role benefits from better organization, clearer records, and easier access to important business information."
      ),
      t(
        "blog-people-p4",
        "A business owner may focus on performance and growth. An accountant may focus on revenue, VAT, payments, and reports. A store manager may focus on stock and product movement. A cashier may focus on orders, payments, and receipts."
      ),
      t(
        "blog-people-p5",
        "Kivunova FMS brings these workflows together with role-based access—so sensitive actions stay controlled and each team member can focus on their job."
      )
    ],
    listItems: [
      t(
        "blog-people-li-1",
        "Business owners — overall performance, growth, and company settings"
      ),
      t(
        "blog-people-li-2",
        "Company administrators — company-wide setup, stores, people, and cross-store visibility"
      ),
      t(
        "blog-people-li-3",
        "Store administrators — day-to-day operations at a specific location"
      ),
      t(
        "blog-people-li-4",
        "Inventory and warehouse staff — stock, products, and movement"
      ),
      t(
        "blog-people-li-5",
        "Sales and procurement staff — orders, purchases, and customer-facing transactions"
      ),
      t("blog-people-li-6", "Analysts — reporting and performance review")
    ]
  },
  {
    id: "industries",
    titleKey: "blog-industries-title",
    titleFallback: "Built for Different Types of Businesses",
    paragraphs: [
      t(
        "blog-industries-p1",
        "Kivunova FMS is flexible enough to support different industries and business models."
      ),
      t("blog-industries-p2", "It can be useful for:")
    ],
    subsections: [
      {
        id: "retail",
        titleKey: "blog-industry-retail-title",
        titleFallback: "Retail shops",
        paragraphs: [
          t(
            "blog-industry-retail-p1",
            "Retail businesses can use Kivunova FMS to track products, manage stock, record sales, generate receipts, and understand revenue."
          )
        ]
      },
      {
        id: "pharmacy",
        titleKey: "blog-industry-pharmacy-title",
        titleFallback: "Pharmacies",
        paragraphs: [
          t(
            "blog-industry-pharmacy-p1",
            "Pharmacies can use the platform to organize stock, monitor product movement, manage orders, and improve visibility into daily sales."
          )
        ]
      },
      {
        id: "warehouse",
        titleKey: "blog-industry-warehouse-title",
        titleFallback: "Warehouses",
        paragraphs: [
          t(
            "blog-industry-warehouse-p1",
            "Warehouses can benefit from stock tracking, product movement visibility, and store or branch organization."
          )
        ]
      },
      {
        id: "wholesale",
        titleKey: "blog-industry-wholesale-title",
        titleFallback: "Wholesalers and distributors",
        paragraphs: [
          t(
            "blog-industry-wholesale-p1",
            "Wholesalers and distributors can manage larger catalogs, purchase and sales workflows, multi-store stock, and supplier relationships in one system."
          )
        ]
      },
      {
        id: "restaurant",
        titleKey: "blog-industry-restaurant-title",
        titleFallback: "Restaurants",
        paragraphs: [
          t(
            "blog-industry-restaurant-p1",
            "Restaurants can use Kivunova FMS to manage sales, stock usage, and business performance."
          )
        ]
      },
      {
        id: "schools",
        titleKey: "blog-industry-schools-title",
        titleFallback: "Schools and education institutions",
        paragraphs: [
          t(
            "blog-industry-schools-p1",
            "School leaders, accountants, bursars, store managers, and auditors can use Kivunova FMS to manage stock movement, payments, purchases, and financial reports."
          )
        ]
      },
      {
        id: "growing",
        titleKey: "blog-industry-growing-title",
        titleFallback: "Growing companies",
        paragraphs: [
          t(
            "blog-industry-growing-p1",
            "Businesses with multiple stores or branches can use Kivunova FMS to centralize operations and improve decision-making."
          )
        ]
      }
    ]
  },
  {
    id: "manual-work",
    titleKey: "blog-manual-work-title",
    titleFallback: "Reducing Manual Work and Human Error",
    paragraphs: [
      t(
        "blog-manual-work-p1",
        "Manual records are often slow and risky. Even a small mistake in a notebook or spreadsheet can lead to larger problems later."
      ),
      t(
        "blog-manual-work-p2",
        "Kivunova FMS helps reduce manual errors by organizing business data in a structured digital system."
      ),
      t("blog-manual-work-p3", "This helps businesses avoid:"),
      t(
        "blog-manual-work-p4",
        "By reducing manual work, teams can spend less time searching for information and more time serving customers, improving operations, and growing the business."
      )
    ],
    listItems: [
      t("blog-manual-work-li-1", "Duplicate records"),
      t("blog-manual-work-li-2", "Missing payments"),
      t("blog-manual-work-li-3", "Incorrect stock counts"),
      t("blog-manual-work-li-4", "Unclear order history"),
      t("blog-manual-work-li-5", "Lost receipts"),
      t("blog-manual-work-li-6", "Miscalculated totals"),
      t("blog-manual-work-li-7", "Hard-to-read reports"),
      t("blog-manual-work-li-8", "Delayed decision-making")
    ]
  },
  {
    id: "visibility",
    titleKey: "blog-visibility-title",
    titleFallback: "Clear Visibility for Better Decisions",
    paragraphs: [
      t(
        "blog-visibility-p1",
        "One of the biggest advantages of Kivunova FMS is visibility."
      ),
      t(
        "blog-visibility-p2",
        "When business information is scattered, decision-making becomes difficult. But when sales, stock, payments, and reports are connected, leaders can understand the business more clearly."
      ),
      t(
        "blog-visibility-p3",
        "Kivunova FMS helps answer important business questions such as:"
      ),
      t(
        "blog-visibility-p4",
        "Better visibility leads to better decisions. Better decisions lead to stronger growth."
      )
    ],
    listItems: [
      t("blog-visibility-li-1", "What is our total revenue?"),
      t("blog-visibility-li-2", "Which products are available?"),
      t("blog-visibility-li-3", "Which store is performing best?"),
      t("blog-visibility-li-4", "How much stock moved recently?"),
      t("blog-visibility-li-5", "Which payments have been recorded?"),
      t("blog-visibility-li-6", "What is our VAT position?"),
      t("blog-visibility-li-7", "Which orders still need follow-up?"),
      t("blog-visibility-li-8", "What should we improve next?")
    ]
  },
  {
    id: "access",
    titleKey: "blog-access-title",
    titleFallback: "Access From Anywhere",
    paragraphs: [
      t(
        "blog-access-p1",
        "Modern businesses need systems that are accessible and flexible. Kivunova FMS is a cloud-based platform—you sign in through a web browser and work from the same system whether you are at the head office, in a store, or reviewing numbers from home."
      ),
      t(
        "blog-access-p2",
        "Users can work from a computer, tablet, or mobile browser depending on their device and access."
      ),
      t(
        "blog-access-p3",
        "Desktop apps for Windows and Mac are also available through the Download apps page, for teams who prefer an installed app that opens the same Kivunova FMS workspace."
      ),
      t(
        "blog-access-p4",
        "An internet connection is required for day-to-day transactional work such as recording sales and updating stock. This keeps your business data synchronized and up to date across your team."
      )
    ]
  },
  {
    id: "security",
    titleKey: "blog-security-title",
    titleFallback: "Security and Peace of Mind",
    paragraphs: [
      t(
        "blog-security-p1",
        "Your business data matters. Kivunova FMS is designed with secure sign-in, controlled access, and permissions that limit who can view or change sensitive company and store information."
      ),
      t(
        "blog-security-p2",
        "When you register, you verify your email and create your own password. If you forget your password, you can recover access through the Forgot password flow."
      ),
      t(
        "blog-security-p3",
        "Each company's information is kept separate, so your records belong to your organization—not mixed with other businesses on the platform."
      )
    ]
  },
  {
    id: "help",
    titleKey: "blog-help-title",
    titleFallback: "Help When You Need It",
    paragraphs: [
      t("blog-help-p1", "You do not have to figure everything out alone."),
      t("blog-help-p2", "Kivunova FMS includes:"),
      t(
        "blog-help-p3",
        "Ask Teta can also guide you while you work inside the platform."
      )
    ],
    listItems: [
      t(
        "blog-help-li-1",
        "An in-app User guide with step-by-step guidance for registration, stores, products, orders, and everyday tasks"
      ),
      t("blog-help-li-2", "A FAQ page with answers to common questions"),
      t(
        "blog-help-li-3",
        "Contact us options for support when you need a person to help"
      )
    ]
  },
  {
    id: "why-choose",
    titleKey: "blog-why-choose-title",
    titleFallback: "Why Choose Kivunova FMS?",
    paragraphs: [
      t(
        "blog-why-choose-p1",
        "Kivunova FMS is built to help businesses move from confusion to clarity."
      ),
      t(
        "blog-why-choose-p2",
        "Businesses should choose Kivunova FMS because it helps them:"
      ),
      t(
        "blog-why-choose-p3",
        "It is more than just a tool. It is a business management partner."
      )
    ],
    listItems: [
      t("blog-why-choose-li-1", "Manage inventory more clearly"),
      t("blog-why-choose-li-2", "Track sales and purchase orders"),
      t("blog-why-choose-li-3", "Record payments, invoices, and receipts"),
      t("blog-why-choose-li-4", "Manage customers and suppliers"),
      t("blog-why-choose-li-5", "Organize teams with role-based access"),
      t(
        "blog-why-choose-li-6",
        "Understand financial performance through dashboards"
      ),
      t("blog-why-choose-li-7", "Reduce paperwork"),
      t("blog-why-choose-li-8", "Improve reporting"),
      t("blog-why-choose-li-9", "Support multiple stores"),
      t("blog-why-choose-li-10", "Work in local languages and currencies"),
      t(
        "blog-why-choose-li-11",
        "Operate with VAT settings suited to Rwanda and the region"
      ),
      t("blog-why-choose-li-12", "Use modern technology for daily operations"),
      t(
        "blog-why-choose-li-13",
        "Get help from Ask Teta when questions come up"
      ),
      t("blog-why-choose-li-14", "Make decisions with confidence"),
      t("blog-why-choose-li-15", "Grow with better structure")
    ]
  },
  {
    id: "future",
    titleKey: "blog-future-title",
    titleFallback: "The Future of Business Management",
    paragraphs: [
      t(
        "blog-future-p1",
        "Business is changing. Customers expect faster service. Owners need better information. Teams need tools that help them work together. Manual systems are no longer enough for businesses that want to grow."
      ),
      t(
        "blog-future-p2",
        "Kivunova FMS helps businesses prepare for the future by giving them a modern platform for financial and inventory management."
      ),
      t(
        "blog-future-p3",
        "With the right system, businesses can reduce confusion, improve control, and focus on what matters most: serving customers and growing sustainably."
      )
    ]
  },
  {
    id: "final-thoughts",
    titleKey: "blog-final-title",
    titleFallback: "Final Thoughts",
    paragraphs: [
      t(
        "blog-final-p1",
        "Kivunova FMS is designed for businesses that want clarity, structure, and growth."
      ),
      t(
        "blog-final-p2",
        "It helps business owners and teams manage products, stores, orders, payments, receipts, customers, suppliers, revenue, VAT, reports, subscriptions, and business insights in one place."
      ),
      t(
        "blog-final-p3",
        "For businesses in Rwanda and across East and Central Africa, Kivunova FMS offers a practical and modern way to simplify operations and understand performance."
      ),
      t(
        "blog-final-p4",
        "If your business is still depending on manual records, scattered spreadsheets, or unclear reports, it may be time to move to a smarter system."
      ),
      t("blog-final-p5", "Kivunova FMS — your business, clearly managed.")
    ]
  }
];

export const getBlogSectionsByIds = (ids: string[]): BlogSection[] =>
  ids
    .map((id) => BLOG_SECTIONS.find((section) => section.id === id))
    .filter((section): section is BlogSection => section != null);

export const BLOG_CTA: {
  titleKey: string;
  titleFallback: string;
  paragraphs: BlogText[];
  listItems: BlogText[];
} = {
  titleKey: "blog-cta-title",
  titleFallback: "Ready to get started?",
  paragraphs: [
    t("blog-cta-p1", "Ready to simplify your business operations?"),
    t(
      "blog-cta-p2",
      "Start managing your inventory, sales, payments, and reports with confidence."
    ),
    t(
      "blog-cta-p3",
      "Visit kivufms.com and discover how Kivunova FMS can help your business grow."
    )
  ],
  listItems: [
    t(
      "blog-cta-li-1",
      "Create your account and explore available plans—including a 7-day free trial where offered"
    ),
    t("blog-cta-li-2", "Read the User guide to get started step by step"),
    t(
      "blog-cta-li-3",
      "Contact us if you have questions about availability in your country or the right plan for your business"
    )
  ]
};
